import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import styled from 'styled-components'
import { useHydrated } from '@/features/shared/components/ClientOnly'
import { ProductImageCarousel } from '@/features/shop/components/ProductDetailCommon/ProductImageCarousel'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { routes } from '@shared/constants/routes'
import { ProductDisclaimer } from '@/features/shop/components/ProductDetailCommon/ProductDisclaimer'
import { InfoBox } from '@/features/shared/components/InfoBox'
import { AvailableStores } from '@/features/shop/components/UngatedProductDetail/AvailableStores'
import { UpdateZipForm } from '@/features/shop/components/UngatedProductDetail/UpdateZipForm'
import { LoadingIndicator } from '@shipt/design-system-loading'
import {
  useStoreId,
  useUserId,
  useUserStoreLocationId,
  useUserZip,
} from '@/features/account/services/User/hooks'
import { trackProductViewed } from '@/features/shared/analytics/products'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { screenSizes } from '@shipt/design-system-themes'
import { ProductDetailPrice } from '@/features/shop/components/ProductDetail/ProductDetailPrice'
import { StoreShowcase } from '@/features/shop/components/UngatedProductDetail/StoreShowcase'
import { getProductHierarchicalCategories } from '@/features/shared/utils/getProductHierarchicalCategories'
import { CategoryBreadcrumb } from '@/features/shop/components/UngatedProductDetail/CategoryBreadcrumb'
import { useQueryUniversalProduct } from '@/features/shop/services/UniversalProduct/queries'
import { type UniversalProductLocation } from '@/features/shop/services/UniversalProduct/types'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import {
  urlBuilder,
  getUniversalCategories,
} from '@/features/shop/services/UniversalCategories/utils'
import { Button } from '@shipt/design-system-buttons'
import { getRouteForAfterAddToCart } from '@/features/shop/components/UngatedProductDetail/utils'
import { spacing } from '@/features/shared/theme/tokens'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { Box, Column } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { Footer } from '@/features/shared/components/Footers/Footer'
import { useAddresses } from '@/features/account/services/Addresses/hooks'
import { useShouldConfirmAddress } from '@/features/account/services/Addresses/useShouldConfirmAddress'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useShallowRouterReplace } from '@/features/shared/hooks/useShallowRouterReplace'
import { PDP_ATC_CLICKED } from '@/features/shop/components/ProductDetail/constants'
import { ProductName } from '@/features/shop/components/ProductDetailCommon/ProductName'
import { DynamicWelcomeModal } from '@/features/entrypoint/components/Modal/WelcomeModal/DynamicWelcomeModal'
import { PDPMembershipBanner } from '@/features/shop/components/ProductDetail/PDPMembershipBanner/PDPMembershipBanner'
import { DynamicZipNotCoveredModal } from '@/features/account/components/Modal/ZipNotCoveredModal/DynamicZipNotCoveredModal'
import { UngatedProductSimilarItems } from '@/features/shop/components/UngatedProductDetail/UngatedProductSimilarItems'
import { ProductDetailRatingsAndReviews } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailRatingsAndReviews'
import { ProductDetailRatingInfo } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailRatingInfo'
import { LazyLoadObserve } from '@/features/shared/components/ScrollIntoView'
import { useParams, usePathname } from 'next/navigation'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { getSearchQueryString } from '@/features/shared/utils/url'
import { ProductWarningsAndDetails } from '@/features/shop/components/ProductDetailCommon/ProductWarningsAndDetails'
import { DynamicConfirmAddressModal } from '@/features/account/components/Modal/ConfirmAddressModal/DynamicConfirmAddressModal'
import { useMutationFetchShoppingStores } from '@/features/shop/services/ShoppingStore/mutations'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { ProductSEOHead } from '@/features/shared/components/SEOHeader/ProductSEOHead'
import { FeedbackBanner } from '@shipt/design-system-banner'
import { type ParamsOf } from '@next-types/routes'

type ParamsType = ParamsOf<'/shop/products/[...slug]' | '/shop/products/[id]'>

export const UngatedProductDetail = ({ zipFromIp }: { zipFromIp: string }) => {
  const router = useSegwayRouter()
  const pathname = usePathname() ?? ''
  const searchParams = useSearchQueryParams()
  const params = useParams<ParamsType>()
  const isSlugParam = params && 'slug' in params

  const hydrated = useHydrated()
  const { openDialog } = useHandleDialog()
  const userZip = useUserZip()
  const addresses = useAddresses()
  const zipParam = String(searchParams.zip ?? '')
  const zip = zipParam || zipFromIp || userZip
  const locationIdParam = Number(searchParams.location_id)
  const universalProductId = (isSlugParam ? params.slug[1] : params?.id) ?? ''
  const atcClicked = String(searchParams[PDP_ATC_CLICKED])
  const { data, isFetching: isUniversalProductFetching } = useQueryUniversalProduct({
    params: {
      region_id: String(searchParams.region_id ?? ''),
      universal_id: universalProductId,
      addresses,
      addressZip: zip,
    },
  })
  const { locations, products, default_product } = data ?? {}
  const userId = useUserId()
  const store_id = useStoreId()
  const store_location_id = useUserStoreLocationId()
  const { mutateAsync: fetchStores } = useMutationFetchShoppingStores()
  const universalCategories = getUniversalCategories()
  const shouldConfirmAddress = useShouldConfirmAddress()

  const [isEditingZip, setIsEditingZip] = useState(false)
  const [isUpdatingProductWithZip, setIsUpdatingProductWithZip] = useState(false)

  const findLocation = (locationId: number) =>
    locations?.find((l) => l.store_location_id === locationId)
  const selectedLocation =
    (locationIdParam && findLocation(locationIdParam)) ||
    (default_product && findLocation(default_product.store_location_id)) ||
    locations?.[0]
  const selectedLocationId = selectedLocation?.store_location_id

  const product = products?.find((p) => p.storeId === selectedLocation?.store_id) ?? products?.[0]

  // we must fetch here to display up-to-date store information
  useQueryShoppingStores({ zip, notifyOnChangeProps: [] })

  const { name = '', brandName, bulletPoints = [], oos = false } = product ?? {}
  const hierarchicalCategories = useMemo(() => {
    if (!product) return []
    return getProductHierarchicalCategories(universalCategories, product)
  }, [universalCategories, product])
  const hasHierarchicalCategories = hierarchicalCategories.length > 0
  const currentCategoryId = hierarchicalCategories.at(-1)?.id ?? 0
  const currentCategoryName = hierarchicalCategories.at(-1)?.name ?? ''

  const { shallowRouterReplace } = useShallowRouterReplace()

  const reviewStats = product?.review_stats

  const showRatingsAndReviews = !!reviewStats?.rating_distribution
  const isUnderline = (reviewStats?.total_ratings ?? 0) > 0

  // Tracking
  useEffect(() => {
    if (product) {
      trackProductViewed({
        product,
        displayFormat: DISPLAY_FORMAT.DETAIL,
      })
    }
  }, [product])

  useEffect(() => {
    // set the initial location_id and zip query params of default_product if they have not been set yet in the url
    if ((!searchParams.location_id || !searchParams.zip) && default_product) {
      shallowRouterReplace({
        zip: default_product.zip_code,
        location_id: default_product.store_location_id,
      })
    }
  }, [shallowRouterReplace, default_product, searchParams.zip, searchParams.location_id])

  const handleRouting = useCallback(async () => {
    const route = getRouteForAfterAddToCart(
      selectedLocation,
      products ?? [],
      store_id,
      store_location_id,
    )
    if (route) {
      router.push(route)
    } else {
      if (selectedLocation?.store_id) {
        await shallowRouterReplace({ up_store_id: selectedLocation.store_id })
      }
      openDialog(DynamicWelcomeModal, { collectAddressOnly: false })
    }
  }, [
    openDialog,
    router,
    selectedLocation,
    store_id,
    store_location_id,
    products,
    shallowRouterReplace,
  ])

  useEffect(() => {
    if (atcClicked === 'true') {
      handleRouting()
      // drop the param entirely so a refresh or a back navigation does not
      // re trigger the routing above and reopen the welcome modal.
      shallowRouterReplace({}, [PDP_ATC_CLICKED])
    }
  }, [atcClicked, handleRouting, shallowRouterReplace])

  const handleAddToCartSubmit = () => {
    if (userId) {
      if (shouldConfirmAddress) {
        return openDialog(DynamicConfirmAddressModal, {
          onContinue: () => {
            handleRouting()
            trackElementClicked({
              message_goal: 'authenticated user',
              content: 'add to cart clicked',
              type: 'button',
            })
          },
        })
      }
      handleRouting()
      trackElementClicked({
        message_goal: 'authenticated user',
        content: 'add to cart clicked',
        type: 'button',
      })
    } else {
      router.push(
        `${routes.SIGN_UP.url}${getSearchQueryString({
          next: `${pathname}?${PDP_ATC_CLICKED}=true&up_store_id=${selectedLocation?.store_id}`,
        })}`,
      )
      trackElementClicked({
        message_goal: 'guest user',
        content: 'add to cart clicked',
        type: 'button',
      })
    }
  }

  const handleStoreSelection = (productLocation: UniversalProductLocation) => {
    if (selectedLocationId === productLocation.store_location_id) return

    shallowRouterReplace({ location_id: productLocation.store_location_id })
  }

  const handleUpdateZipSubmit = async (zipcode: string) => {
    setIsUpdatingProductWithZip(true)

    // Verify zipcode is within coverage area
    try {
      await fetchStores({ address: { zip_code: zipcode } })
    } catch (e) {
      setIsUpdatingProductWithZip(false)
      return openDialog(DynamicZipNotCoveredModal, { zip: zipcode })
    }
    // we want to remove region_id from the query params when updating the zip
    // since region_id takes precedence over zip within the universal product service
    await shallowRouterReplace({ zip: zipcode }, ['region_id'])
    setIsUpdatingProductWithZip(false)
    setIsEditingZip(false)
  }

  const addZipSubmit = (content: string) => {
    setIsEditingZip(true)

    trackElementClicked({
      message_goal: 'Zip code text button clicked',
      type: 'button',
      content,
    })
  }

  const scrollToRatingsAndReview = () => {
    const ratingAndReviewsSection = document.getElementById('ratingsAndReviewsSection')

    if (ratingAndReviewsSection) {
      const elementPosition = ratingAndReviewsSection.getBoundingClientRect().top + window.scrollY

      window.scrollTo({
        top: elementPosition - 70,
        behavior: 'smooth',
      })
    }
  }

  const renderProductDetails = () => {
    // Loading
    if (isUniversalProductFetching && !isUpdatingProductWithZip) {
      return <LoadingIndicator size="xl" />
    }

    // Adding/Editing zip
    if (isEditingZip || (!zip && !locations?.length)) {
      return (
        <>
          <UpdateZipForm
            isLoading={isUpdatingProductWithZip}
            onUpdateZipSubmit={handleUpdateZipSubmit}
          />
          {!userId && <PDPMembershipBanner />}
          <StoreShowcase />
        </>
      )
    }

    // Product details and stores
    if (product && locations && locations.length > 0) {
      return (
        <>
          <ProductDetailPrice product={product} />
          {!userId && <PDPMembershipBanner />}
          <Button disabled={oos} fullWidth onClick={handleAddToCartSubmit}>
            Add to cart
          </Button>
          {bulletPoints.length > 0 && <InfoBox text={bulletPoints.join('. ')} />}
          <AvailableStores
            locations={locations}
            zip={zip}
            onUpdateZipClick={() => addZipSubmit('updating')}
            selectedLocation={selectedLocation}
            updateSelectedLocation={handleStoreSelection}
          />
        </>
      )
    }

    // No product details for zip
    return (
      <>
        <FeedbackBanner hasIcon title={`Product not available in: ${zip}`} variant="warning" />
        <Button onClick={() => addZipSubmit('updating')} fullWidth>
          Update ZIP code
        </Button>
        <>
          {!!hasHierarchicalCategories && (
            <ButtonLink
              href={urlBuilder({
                universalCategories,
                category: hierarchicalCategories.at(-1),
                zip,
              })}
              variant="secondary"
              fullWidth
            >
              Shop similar items
            </ButtonLink>
          )}
          {!userId && <PDPMembershipBanner />}
          <StoreShowcase />
        </>
      </>
    )
  }

  if (!product) return null

  const canonicalUrl = `https://www.shipt.com${getProductPageUrl({
    universal_id: universalProductId,
    ungated: true,
  })}`

  const breadcrumbCategories = hierarchicalCategories.map((category, index) => ({
    '@type': 'ListItem',
    position: index + 2, // position starts from 2 because 1 is reserved for Home
    name: category.name,
    item: 'https://www.shipt.com' + urlBuilder({ universalCategories, category }),
  }))
  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    '@context': 'https://schema.org',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.shipt.com',
      },
      ...breadcrumbCategories,
      {
        '@type': 'ListItem',
        position: breadcrumbCategories.length + 2, // +2 because we have Home and the last item
        name: product.name,
        item: canonicalUrl,
      },
    ],
  }

  return (
    <>
      <ProductSEOHead breadcrumbs={breadcrumbs} {...product} />
      <GridLayoutContainer>
        <Box paddingTop="lg" paddingBottom="xxl">
          {!!hasHierarchicalCategories && (
            <CategoryBreadcrumb categories={hierarchicalCategories} />
          )}
          <TopSectionWrapper>
            <LeftWrapper>
              <ProductImageCarousel product={product} />
            </LeftWrapper>
            <RightWrapper>
              {!!brandName && <BrandName strong>{brandName}</BrandName>}
              <Column spacing="lg">
                <ProductName name={name} />
                {!!showRatingsAndReviews && (
                  <ProductDetailRatingInfo
                    scrollToSection={scrollToRatingsAndReview}
                    avgRating={reviewStats.average_rating}
                    isUnderline={isUnderline}
                    totalRatings={reviewStats.total_ratings}
                  />
                )}
                {renderProductDetails()}
              </Column>
            </RightWrapper>
          </TopSectionWrapper>
          <ProductWarningsAndDetails product={product} />
          {!!hydrated && (
            <>
              <UngatedProductSimilarItems
                currentProductUniversalId={product.universal_id}
                categoryId={currentCategoryId}
                categoryName={currentCategoryName}
              />
              {!!showRatingsAndReviews && (
                <div id="ratingsAndReviewsSection">
                  <LazyLoadObserve minHeight={150}>
                    <ProductDetailRatingsAndReviews productId={product.id} />
                  </LazyLoadObserve>
                </div>
              )}
              <ProductDisclaimer hasFeaturedPromotion={false} />
            </>
          )}
        </Box>
      </GridLayoutContainer>
      <Footer disclaimer="*Offer valid for new customers only, returning customers are ineligible. 14 day trial will renew at the standard membership rate at the end of the trial. Cancellation available free of charge anytime during trial. Offer is subject to Shipt Promotion Terms and Conditions." />
    </>
  )
}

const TopSectionWrapper = styled.section`
  display: flex;
  flex-wrap: wrap;
`

const LeftWrapper = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 1.5rem;
  padding-right: 16px;

  @media ${screenSizes.tablet} {
    width: 60%;
  }
`

const RightWrapper = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  @media ${screenSizes.tablet} {
    width: 40%;
    padding-left: 64px;
    max-width: 27rem;
    margin-left: auto;
  }
`

const BrandName = styled(Body)`
  color: ${({ theme }) => theme.purple500};
  margin-bottom: ${spacing('xs')};
  text-transform: capitalize;
`
